import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Spin,
  message,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import warehouseService from '@/services/warehouse.service';
import warehouseLocationService, {
  type WarehouseLocation,
} from '@/services/warehouseLocation.service';

const { Title, Text } = Typography;

const LOCATION_TYPES = ['Zone', 'Aisle', 'Rack', 'Shelf', 'Bin'];

const WarehouseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WarehouseLocation | null>(null);

  const { data: warehouse, isLoading: whLoading } = useQuery({
    queryKey: ['warehouse', id],
    queryFn: () => warehouseService.getById(id!),
    enabled: !!id,
  });

  const {
    data: locations = [],
    isLoading: locLoading,
  } = useQuery({
    queryKey: ['warehouse-locations', id],
    queryFn: () => warehouseLocationService.listByWarehouse(id!),
    enabled: !!id,
  });

  const parentOptions = useMemo(
    () =>
      locations
        .filter((l) => !editing || l.id !== editing.id)
        .map((l) => ({
          value: l.id,
          label: `${l.location_code} — ${l.location_name}`,
        })),
    [locations, editing]
  );

  useEffect(() => {
    if (!modalOpen) return;
    if (editing) {
      form.setFieldsValue({
        location_code: editing.location_code,
        location_name: editing.location_name,
        location_type: editing.location_type || 'Bin',
        parent_location_id: editing.parent_location_id || undefined,
        capacity_quantity: Number(editing.capacity_quantity ?? 0),
        is_active: editing.is_active !== false,
      });
      return;
    }
    form.resetFields();
    form.setFieldsValue({ location_type: 'Bin', is_active: true, capacity_quantity: 0 });
  }, [modalOpen, editing, form]);

  const saveMut = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const payload = {
        location_code: String(values.location_code).trim(),
        location_name: String(values.location_name).trim(),
        location_type: (values.location_type as string) || 'Bin',
        parent_location_id: (values.parent_location_id as string) || null,
        capacity_quantity: Number(values.capacity_quantity ?? 0),
        is_active: values.is_active !== false,
      };
      if (editing) {
        return warehouseLocationService.update(editing.id, payload);
      }
      return warehouseLocationService.create(id!, payload);
    },
    onSuccess: () => {
      message.success(editing ? 'Bin updated' : 'Bin created');
      queryClient.invalidateQueries({ queryKey: ['warehouse-locations', id] });
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (locId: string) => warehouseLocationService.remove(locId),
    onSuccess: () => {
      message.success('Bin deleted');
      queryClient.invalidateQueries({ queryKey: ['warehouse-locations', id] });
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Delete failed'),
  });

  const columns: ColumnsType<WarehouseLocation> = [
    { title: 'Code', dataIndex: 'location_code', key: 'code', width: 140 },
    { title: 'Name', dataIndex: 'location_name', key: 'name', width: 220 },
    {
      title: 'Type',
      dataIndex: 'location_type',
      key: 'type',
      width: 100,
      render: (t: string) => <Tag>{t || 'Bin'}</Tag>,
    },
    {
      title: 'Parent',
      dataIndex: 'parent_location_id',
      key: 'parent',
      width: 160,
      render: (pid: string | null) => {
        if (!pid) return '—';
        const p = locations.find((l) => l.id === pid);
        return p ? p.location_code : pid.slice(0, 8);
      },
    },
    {
      title: 'Capacity',
      dataIndex: 'capacity_quantity',
      key: 'cap',
      width: 100,
      render: (v: number) => (Number(v) ? Number(v) : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'active',
      width: 100,
      render: (v: boolean) =>
        v !== false ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(row);
              setModalOpen(true);
            }}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              Modal.confirm({
                title: 'Delete bin',
                content: `Delete ${row.location_code}?`,
                okType: 'danger',
                onOk: () => deleteMut.mutateAsync(row.id),
              })
            }
          />
        </Space>
      ),
    },
  ];

  if (whLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="danger">Warehouse not found</Text>
          <br />
          <Button type="link" onClick={() => navigate('/scm/warehouse/warehouses')}>
            Back
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <div>
              <Space>
                <Link to="/scm/warehouse/warehouses">
                  <Button type="text" icon={<ArrowLeftOutlined />} />
                </Link>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {warehouse.warehouse_name}
                  </Title>
                  <Text type="secondary">
                    {warehouse.warehouse_code} · Bins / locations
                  </Text>
                </div>
              </Space>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              Add bin
            </Button>
          </Space>

          <Table<WarehouseLocation>
            rowKey="id"
            loading={locLoading}
            columns={columns}
            dataSource={locations}
            scroll={{ x: 900 }}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          />
        </Space>
      </Card>

      <Modal
        title={editing ? 'Edit bin' : 'Add bin'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={saveMut.isPending}
        destroyOnClose
        okText={editing ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => saveMut.mutate(values)}
        >
          <Form.Item
            name="location_code"
            label="Code"
            rules={[{ required: true, message: 'Code required' }]}
          >
            <Input placeholder="e.g. A-01-01" disabled={!!editing} />
          </Form.Item>
          <Form.Item
            name="location_name"
            label="Name"
            rules={[{ required: true, message: 'Name required' }]}
          >
            <Input placeholder="e.g. Aisle A Rack 1 Bin 1" />
          </Form.Item>
          <Form.Item name="location_type" label="Type" rules={[{ required: true }]}>
            <Select options={LOCATION_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="parent_location_id" label="Parent location">
            <Select allowClear showSearch optionFilterProp="label" options={parentOptions} />
          </Form.Item>
          <Form.Item name="capacity_quantity" label="Capacity qty">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" valuePropName="checked">
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WarehouseDetailPage;
